import React, { useRef, useState } from 'react';
import { Copy, ArrowSquareOut } from 'phosphor-react';
import StatusTag, { StatusType } from './StatusTag';
import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';

type OrderOrigin = 'manual' | 'pdf' | 'email' | 'whatsapp';

const ORIGIN_LABEL: Record<OrderOrigin, string> = {
  manual: 'Creada a mano',
  pdf: 'Creada desde OC',
  email: 'Correo',
  whatsapp: 'WhatsApp',
};

function fileOriginLabel(fileName: string): string {
  const ext = fileName.split('.').pop()?.toUpperCase() ?? '';
  return `Creada desde ${ext}`;
}

const PRICE_STATUS_LABEL: Record<'yellow' | 'green' | 'blue', string> = {
  yellow: 'Algunos precios tomados de Kame',
  green: 'Todos los precios de la lista o editados a mano',
  blue: 'Algunos precios tomados de la OC',
};

interface OrderCardProps {
  orderId: string;
  company: string;
  customer: string;
  productCount: number;
  total: string;
  status: StatusType;
  hasUnread?: boolean;
  date?: string;
  kameoneFolio?: string;
  documentType?: string | null;
  origin?: OrderOrigin;
  addressName?: string | null;
  sourceFileName?: string | null;
  onClick?: () => void;
  onDuplicate?: () => void;
  isHighlighted?: boolean;
  /** 'yellow' = ≥1 item from Kame/catalog (pulsating); 'green' = all from price list; 'blue' = some from OC, none from Kame */
  priceStatus?: 'yellow' | 'green' | 'blue';
}

export const OrderCard: React.FC<OrderCardProps> = ({
  orderId,
  company,
  customer,
  productCount,
  total,
  status,
  hasUnread = false,
  date,
  kameoneFolio,
  documentType,
  origin,
  addressName,
  sourceFileName,
  onClick,
  onDuplicate,
  isHighlighted = false,
  priceStatus,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`relative group bg-background border border-border rounded-lg p-3 cursor-pointer hover:bg-black/5 transition-colors duration-base ease-smooth${isHighlighted ? ' order-card-highlight' : ''}`}
      onClick={onClick}
      onMouseEnter={() => priceStatus && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <p className={`font-sans ${hasUnread ? 'font-semibold' : 'font-medium'} text-base text-text tracking-[-0.16px]`}>
            {orderId}
          </p>
          {priceStatus && (
            <Tooltip open={open}>
              <TooltipTrigger asChild>
                {priceStatus === 'yellow' ? (
                  <div className="relative flex h-2 w-2 items-center justify-center">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-yellow-300/60 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-300/60" />
                  </div>
                ) : (
                  <span className={`relative inline-flex h-2 w-2 rounded-full ${priceStatus === 'green' ? 'bg-green-300/60' : 'bg-blue-300/60'}`} />
                )}
              </TooltipTrigger>
              <TooltipContent>{PRICE_STATUS_LABEL[priceStatus]}</TooltipContent>
            </Tooltip>
          )}
          {kameoneFolio && status !== 'pendiente' && (
            <span className="font-sans text-xs font-medium text-text-subtle bg-muted px-1.5 py-0.5 rounded">
              Folio: {kameoneFolio}
            </span>
          )}
          {onDuplicate && (
            <button
              className="flex items-center gap-0.5 font-sans text-xs text-primary/70 hover:text-primary hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              aria-label="Duplicar pedido"
            >
              Duplicar
              <Copy size={12} weight="bold" />
            </button>
          )}
          {kameoneFolio && status !== 'pendiente' && (
            <a
              href={`https://www.kameone.cl/Documento/Index?tipoDoc=V&folio=${kameoneFolio}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 font-sans text-xs text-primary/70 hover:text-primary hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              Abrir en Kame
              <ArrowSquareOut size={12} weight="bold" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <StatusTag status={status} />
        </div>
      </div>

      {/* Sub-header row: company name */}
      <div className="flex items-center mb-1">
        <p className="font-sans font-medium text-sm text-text tracking-[-0.14px] truncate flex-1">
          {company}
        </p>
      </div>
      {addressName && (
        <p className="font-sans font-normal text-xs text-text-subtle tracking-[-0.12px] truncate">
          {addressName}
        </p>
      )}

      {/* Content */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
          <p className="font-sans font-normal text-xs text-text-subtle">
            {productCount} productos
          </p>
          <p className="font-sans font-semibold text-sm text-text">
            {total}
          </p>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="font-sans font-normal text-xs text-text-subtle">
            {date}
          </p>
          <div className="flex items-center gap-1.5">
            {documentType && status !== 'pendiente' && (
              <span className="font-sans text-xs text-text-subtle bg-muted px-1.5 py-0.5 rounded">
                {documentType.toLowerCase().includes('guía') || documentType.toLowerCase().includes('guia') ? 'Guía' : 'Factura'}
              </span>
            )}
            {origin && (
              <span className="font-sans text-xs text-text-subtle bg-muted px-1.5 py-0.5 rounded">
                {sourceFileName ? fileOriginLabel(sourceFileName) : ORIGIN_LABEL[origin]}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
